import { Link, useNavigate } from '@tanstack/react-router';
import { CircleCheck, Plus } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useForm, useFormContext, useWatch, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { CreateOrganizationResponse } from '@nucleus-commerce/api-client/platform';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { timeZoneLabel, timeZoneOptions } from '@/shared/time-zones';

import { toCreateOrganizationRequest } from '../api/mappers';
import { useProvisionOrganization } from '../hooks/use-provision-organization';
import {
  emptyProvisionForm,
  provisionSchema,
  type ProvisionInput,
  type ProvisionOutput,
} from '../schemas/provision-schema';

import { ProvisionWarnings } from './provision-warnings';

interface FieldSpec {
  name: Path<ProvisionInput>;
  label: string;
  hint?: string;
  type?: 'text' | 'email' | 'password';
  autoComplete?: string;
}

/** Invite by email (default) or set the owner's first password here. */
function OwnerAccessFields() {
  const { t } = useTranslation('organizations');
  const { control } = useFormContext<ProvisionInput>();
  const sendInvite = useWatch({ control, name: 'owner.sendInvite' });
  return (
    <>
      <FormField
        control={control}
        name="owner.sendInvite"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border p-3">
            <div className="flex flex-col gap-1">
              <FormLabel>{t('fields.owner.sendInvite')}</FormLabel>
              <FormDescription>{t('fields.owner.sendInviteHint')}</FormDescription>
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />
      {sendInvite ? null : (
        <FormField
          control={control}
          name="owner.password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.owner.password')}</FormLabel>
              <FormControl>
                <PasswordInput autoComplete="new-password" {...field} />
              </FormControl>
              <FormDescription>{t('fields.owner.passwordHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}

/** Every IANA zone the browser knows; the backend validates by resolving the name. */
function TimeZoneField() {
  const { t } = useTranslation('organizations');
  const { control } = useFormContext<ProvisionInput>();
  const zones = useMemo(() => timeZoneOptions(), []);
  return (
    <FormField
      control={control}
      name="store.timezone"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('fields.store.timezone')}</FormLabel>
          <Select value={field.value} onValueChange={field.onChange}>
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent className="max-h-72">
              {zones.map((zone) => (
                <SelectItem key={zone} value={zone}>
                  {timeZoneLabel(zone)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormDescription>{t('fields.store.timezoneHint')}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** What happened that the operator should know before moving on. */
function ProvisionResult({
  result,
  currency,
  onClose,
}: {
  result: CreateOrganizationResponse;
  currency: string;
  onClose: () => void;
}) {
  const { t } = useTranslation(['organizations', 'common']);
  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('organizations:provisioned.title')}</DialogTitle>
        <DialogDescription>
          {t('organizations:provisioned.description', { name: result.organization.name })}
        </DialogDescription>
      </DialogHeader>
      <Alert role="status">
        <CircleCheck aria-hidden />
        <AlertTitle>{result.owner.email}</AlertTitle>
        <AlertDescription>
          {!result.owner.created
            ? t('organizations:provisioned.ownerExisting', { email: result.owner.email })
            : result.owner.invited
              ? t('organizations:provisioned.ownerInvited', { email: result.owner.email })
              : t('organizations:provisioned.ownerPassword', { email: result.owner.email })}
        </AlertDescription>
      </Alert>
      <ProvisionWarnings warnings={result.warnings} currency={currency} />
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
        <Button asChild>
          <Link
            to="/organizations/$organizationId"
            params={{ organizationId: result.organization.id }}
            onClick={onClose}
          >
            {t('organizations:provisioned.open')}
          </Link>
        </Button>
      </DialogFooter>
    </>
  );
}

/** Provisions organization + owner + first store in one call; the API creates all three or nothing. */
export function ProvisionOrganizationDialog({
  tour,
}: {
  /** Which page's tour points at this instance. */
  tour?: 'list' | 'overview';
} = {}) {
  const { t } = useTranslation(['organizations', 'common']);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{
    response: CreateOrganizationResponse;
    currency: string;
  } | null>(null);
  const navigate = useNavigate();
  const provision = useProvisionOrganization();
  const form = useForm<ProvisionInput, unknown, ProvisionOutput>({
    resolver: useI18nResolver(provisionSchema),
    defaultValues: emptyProvisionForm(),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setResult(null);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const response = await provision.mutateAsync(toCreateOrganizationRequest(values));
      form.reset(emptyProvisionForm());
      // Nothing to point out: straight to the new organization, as before.
      if (response.warnings.length === 0 && response.owner.created) {
        setOpen(false);
        await navigate({
          to: '/organizations/$organizationId',
          params: { organizationId: response.organization.id },
        });
        return;
      }
      setResult({ response, currency: values.store.defaultCurrency });
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  const sections: Array<{ title: string; fields: FieldSpec[]; extra?: ReactNode }> = [
    {
      title: t('organizations:sections.organization'),
      fields: [
        { name: 'name', label: t('organizations:fields.name') },
        {
          name: 'slug',
          label: t('organizations:fields.slug'),
          hint: t('organizations:fields.slugHint'),
        },
        {
          name: 'billingEmail',
          label: t('organizations:fields.billingEmail'),
          hint: t('organizations:fields.billingEmailHint'),
          type: 'email',
        },
      ],
    },
    {
      title: t('organizations:sections.owner'),
      fields: [
        { name: 'owner.name', label: t('organizations:fields.owner.name'), autoComplete: 'off' },
        {
          name: 'owner.email',
          label: t('organizations:fields.owner.email'),
          hint: t('organizations:fields.owner.emailHint'),
          type: 'email',
          autoComplete: 'off',
        },
      ],
      extra: <OwnerAccessFields />,
    },
    {
      title: t('organizations:sections.store'),
      fields: [
        {
          name: 'store.name',
          label: t('organizations:fields.store.name'),
          hint: t('organizations:fields.store.nameHint'),
        },
        {
          name: 'store.slug',
          label: t('organizations:fields.store.slug'),
          hint: t('organizations:fields.store.slugHint'),
        },
        {
          name: 'store.defaultCurrency',
          label: t('organizations:fields.store.defaultCurrency'),
          hint: t('organizations:fields.store.defaultCurrencyHint'),
        },
        { name: 'store.countryCode', label: t('organizations:fields.store.countryCode') },
      ],
      extra: <TimeZoneField />,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        asChild
        data-tour={
          tour === 'list'
            ? 'platform.organizations.provision'
            : tour === 'overview'
              ? 'platform.overview.provision'
              : undefined
        }
      >
        <Button>
          <Plus aria-hidden />
          {t('organizations:provision')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        {result ? (
          <ProvisionResult
            result={result.response}
            currency={result.currency}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('organizations:provision')}</DialogTitle>
              <DialogDescription>{t('organizations:empty.description')}</DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={onSubmit} className="flex flex-col gap-6" noValidate>
                {sections.map((section) => (
                  <fieldset key={section.title} className="flex flex-col gap-4">
                    <legend className="mb-2 text-sm font-medium">{section.title}</legend>
                    {section.fields.map((spec) => (
                      <FormField
                        key={spec.name}
                        control={form.control}
                        name={spec.name}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{spec.label}</FormLabel>
                            <FormControl>
                              <Input
                                type={spec.type ?? 'text'}
                                autoComplete={spec.autoComplete}
                                {...field}
                                value={typeof field.value === 'string' ? field.value : ''}
                              />
                            </FormControl>
                            {spec.hint ? <FormDescription>{spec.hint}</FormDescription> : null}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ))}
                    {section.extra}
                  </fieldset>
                ))}
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                    {t('common:actions.cancel')}
                  </Button>
                  <Button type="submit" disabled={provision.isPending}>
                    {t('common:actions.create')}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
